import { useCallback, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'

import { sentry } from 'legacy/utils/sentry'

import { type Location } from '../../domain/Location'
import {
  type DeleteDriverLocationParams,
  type DriverLocationParams,
} from '../../infra/ShipmentRepository'
import { addDriverLocation } from '../../use-cases/addDriverLocation'
import { deleteDriverLocation } from '../../use-cases/deleteDriverLocation'
import { getDriverLocations } from '../../use-cases/getDriverLocations'

type State = {
  locations?: Location[]
  addLocationStatus: RequestStatus
  deleteLocationStatus: RequestStatus
  getLocationsStatus: RequestStatus
}

const initialState = {
  getLocationsStatus: RequestStatus.Initial,
  addLocationStatus: RequestStatus.Initial,
  deleteLocationStatus: RequestStatus.Initial,
}

export function useDriverLocation() {
  const [{ locations, addLocationStatus, deleteLocationStatus, getLocationsStatus }, setState] =
    useState<State>(initialState)

  const addLocation = useCallback(async (params: DriverLocationParams) => {
    setState(previous => ({ ...previous, addLocationStatus: RequestStatus.Pending }))

    try {
      await addDriverLocation(params)
      setState(previous => ({ ...previous, addLocationStatus: RequestStatus.Done }))
    } catch (error) {
      sentry.log(error)
      setState(previous => ({ ...previous, addLocationStatus: RequestStatus.Error }))
    }
  }, [])

  const getLocations = useCallback(async (shipmentUUID: string, { signal }: AbortController) => {
    setState(previous => ({ ...previous, getLocationsStatus: RequestStatus.Pending }))

    try {
      const response = await getDriverLocations(shipmentUUID, { signal })

      setState(previous => ({
        ...previous,
        locations: response,
        getLocationsStatus: RequestStatus.Done,
      }))
    } catch (error) {
      setState(previous => ({
        ...previous,
        locations: undefined,
        getLocationsStatus: RequestStatus.Error,
      }))
    }
  }, [])

  const deleteLocation = useCallback(async (params: DeleteDriverLocationParams) => {
    setState(previous => ({ ...previous, deleteLocationStatus: RequestStatus.Pending }))

    try {
      await deleteDriverLocation(params)
      setState(previous => ({ ...previous, deleteLocationStatus: RequestStatus.Done }))
    } catch (error) {
      sentry.log(error)
      setState(previous => ({ ...previous, deleteLocationStatus: RequestStatus.Error }))
    }
  }, [])

  return {
    addLocation,
    getLocations,
    deleteLocation,
    locations,
    addLocationStatus,
    deleteLocationStatus,
    getLocationsStatus,
  }
}
